<template>
  <RevDrawer
    :back-button-label="i18n(translations.back)"
    :close-button-label="i18n(translations.cancel)"
    :has-back-button="shouldShowBackButton"
    :name="MODAL_NAME"
    :title="modalTitle"
    @back="handleBack"
    @close="handleClose"
  >
    <template #body>
      <List
        v-if="selectedListItem === null"
        @select-list-item="handleSelectListItem"
      />

      <div v-if="shouldShowForm">
        <RevInfoBlock
          v-if="apiError"
          class="mt-16"
          :icon="IconWarning"
          :title="apiError.title"
          variant="danger"
        >
          {{ apiError.detail }}
        </RevInfoBlock>

        <ImportImei
          v-if="selectedListItem === 'imei-numbers'"
          @submit="handleSubmit"
        />
        <ImportTrackingNumbers
          v-if="selectedListItem === 'tracking-numbers'"
          @submit="handleSubmit"
        />
      </div>
    </template>

    <template #footer v-if="shouldShowForm">
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          data-qa="submit-button"
          :form="FORM_ID"
          full-width="always"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.submit) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { postMerchantOrdersImport } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import { FORM_ID, MODAL_NAME } from './ImportDrawer.constants'
import translations from './ImportDrawer.translations'
import type { FormValues, ListItem } from './ImportDrawer.types'
import ImportImei from './components/ImportImei/ImportImei.vue'
import ImportTrackingNumbers from './components/ImportTrackingNumbers/ImportTrackingNumbers.vue'
import List from './components/List/List.vue'

const i18n = useI18n()

const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)

const selectedListItem = ref<ListItem | null>(null)
const shouldShowForm = computed(() => Boolean(selectedListItem.value))
const shouldShowBackButton = computed(() => Boolean(selectedListItem.value))

const modalTitle = computed(() => {
  const titles: Record<ListItem, string> = {
    'imei-numbers': i18n(translations.importIMEIsTitle),
    'tracking-numbers': i18n(translations.importTrackingNumbersTitle),
  }

  return selectedListItem.value
    ? titles[selectedListItem.value]
    : i18n(translations.title)
})

function reset() {
  apiError.value = null
  selectedListItem.value = null
}

async function handleSubmit(formValues: FormValues) {
  try {
    isLoading.value = true

    const body = new FormData()

    body.append('csvFile', formValues.files[0])
    body.append('delimiter', formValues.delimiter)
    if (formValues.encoding) {
      body.append('encoding', formValues.encoding)
    }

    await $httpFetch(postMerchantOrdersImport, {
      pathParams: {
        importType: selectedListItem.value,
      },
      body,
    })

    openSuccessToast({
      title: i18n(translations.fileImportSuccessTitle),
      content: i18n(translations.fileImportSuccessDescription),
    })

    reset()
    closeModal(MODAL_NAME)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}

function handleBack() {
  reset()
}

function handleClose() {
  reset()
}

function handleCancel() {
  reset()
  closeModal(MODAL_NAME)
}

function handleSelectListItem(listItem: ListItem) {
  selectedListItem.value = listItem
}
</script>
